import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ShieldCheck, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PrivacyConsentGate = () => {
  const { user, updateUser, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [policyUrl, setPolicyUrl] = useState('/privacy-policy');
  const [policyVersion, setPolicyVersion] = useState('1.0');
  const [isDraftLegal, setIsDraftLegal] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/signup-config`)
      .then((res) => {
        if (res.data.privacy_policy_url) setPolicyUrl(res.data.privacy_policy_url);
        if (res.data.privacy_policy_version) setPolicyVersion(res.data.privacy_policy_version);
        setIsDraftLegal(res.data.legal_docs_are_drafts !== false);
      })
      .catch(() => {});
  }, []);

  const isExternal = policyUrl.startsWith('http');

  const handleAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/accept-privacy-policy`);
      try {
        await axios.post(`${API_URL}/api/auth/me/consents`, { marketing_consent: marketingOptIn, screen: 'consent_gate', notice_key: 'marketing_checkbox' });
      } catch (e) { /* optional consent - never blocks */ }
      if (!isDraftLegal) {
        // Real policy accepted: clear upload-consent memory so AI/research consents are re-captured against it
        try { localStorage.removeItem('insul_upload_consent_done'); } catch (e) { /* non-blocking */ }
      }
      updateUser({ ...user, needs_privacy_consent: false });
      toast.success(isDraftLegal ? 'Thank you — your acknowledgement has been recorded.' : 'Thank you — your consent has been recorded.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record consent. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-primary/5 to-transparent">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg"
        data-testid="reconsent-gate"
      >
        <div className="bg-card text-card-foreground rounded-2xl border border-border/50 shadow-lg p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {isDraftLegal ? 'Pre-release Acknowledgement' : 'Privacy Policy Update'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isDraftLegal ? 'Platform in development — legal terms not yet finalised' : `Version ${policyVersion}`}
              </p>
            </div>
          </div>

          <p className="text-base text-foreground/90 leading-relaxed mb-6">
            {isDraftLegal ? (
              <>Insul Nutrients is in active development and its Terms of Use and Privacy Policy are drafts
              that are not yet in effect. In the meantime, your data (email, profile details, meal photos and
              nutrition logs) is collected only to provide the service, meal photos are deleted after 7 days,
              and you can export your data or delete your account at any time. You will be asked to review and
              accept the final Terms and Privacy Policy when they are published.</>
            ) : (
              <>Before you continue, we need your consent to our Privacy Policy. It explains what
              information we collect (your email, profile details, meal photos and nutrition logs),
              how it is used to power your food diary, and that meal photos are permanently deleted
              after 7 days. Please read it in full before accepting.</>
            )}
          </p>

          <div className="mb-6">
            {isExternal ? (
              <a href={policyUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline" data-testid="reconsent-policy-link">
                Read the full Privacy Policy
              </a>
            ) : (
              <Link to={policyUrl} target="_blank" className="text-primary font-semibold hover:underline" data-testid="reconsent-policy-link">
                Read the full Privacy Policy
              </Link>
            )}
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-background/50 p-4 mb-6" data-testid="reconsent-section">
            <Checkbox
              id="reconsent-checkbox"
              data-testid="reconsent-checkbox"
              checked={checked}
              onCheckedChange={(v) => setChecked(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="reconsent-checkbox" className="text-sm font-normal leading-snug text-foreground/90 cursor-pointer">
              {isDraftLegal
                ? 'I acknowledge the pre-release notice above'
                : 'I have read and accept the Privacy Policy'}
            </Label>
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-dashed border-border/50 bg-background/30 p-4 mb-6" data-testid="reconsent-marketing-section">
            <Checkbox
              id="reconsent-marketing-checkbox"
              data-testid="reconsent-marketing-checkbox"
              checked={marketingOptIn}
              onCheckedChange={(v) => setMarketingOptIn(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="reconsent-marketing-checkbox" className="text-sm font-normal leading-snug text-muted-foreground cursor-pointer">
              Optional: send me daily meal reminder emails. You can unsubscribe anytime — this never affects your account or access.
            </Label>
          </div>

          <Button
            data-testid="reconsent-accept-button"
            onClick={handleAccept}
            disabled={!checked || submitting}
            className="w-full rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-primary to-[#8FD14F]"
          >
            {submitting ? 'Recording...' : isDraftLegal ? 'Acknowledge & Continue' : 'Agree & Continue'}
          </Button>

          <button
            data-testid="reconsent-logout-button"
            onClick={logout}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log out instead
          </button>
        </div>
      </motion.div>
    </div>
  );
};
